/**
 * Every question carries exactly one of `label` and `labelKey`, and which one says who owns the
 * wording.
 *
 * A question that names the collection, its partner or its campaign can only be worded by the
 * server that holds those values, so it arrives as `label`. A question whose wording is the same
 * sentence for every collection forever is product copy, lives in `packages/i18n`, and arrives as
 * the key it is filed under. {@see feedbackQuestionLabel} is what turns either into a sentence.
 */
export interface FeedbackSurveyQuestionBase {
  key: string;
  label?: string | null;
  labelKey?: string | null;
  required: boolean;
  type: string;
}

type FeedbackSurveyQuestionScaleBase = FeedbackSurveyQuestionBase & {
  type: 'SCALE';
  min: number;
  max: number;
};

export type FeedbackSurveyQuestionScale
  = | (FeedbackSurveyQuestionScaleBase & { minLabel: string; maxLabel: string })
    | FeedbackSurveyQuestionScaleBase;

export type FeedbackSurveyQuestionScaleLoose = FeedbackSurveyQuestionScaleBase & {
  minLabel?: string | null;
  maxLabel?: string | null;
};

const hasScaleEndLabelText = (value: string | null | undefined): boolean =>
  value != null && value.trim() !== '';

export const areFeedbackSurveyScaleEndLabelsPaired = (
  minLabel: string | null | undefined,
  maxLabel: string | null | undefined,
): boolean => hasScaleEndLabelText(minLabel) === hasScaleEndLabelText(maxLabel);

export const normalizeFeedbackSurveyScaleQuestion = (
  question: FeedbackSurveyQuestionScaleLoose,
): FeedbackSurveyQuestionScale => {
  if (
    hasScaleEndLabelText(question.minLabel)
    && hasScaleEndLabelText(question.maxLabel)
    && question.minLabel != null
    && question.maxLabel != null
  ) {
    return {
      ...question,
      minLabel: question.minLabel,
      maxLabel: question.maxLabel,
    };
  }

  const { minLabel: _minLabel, maxLabel: _maxLabel, ...rest } = question;
  return rest;
};

export interface FeedbackSurveyQuestionYesNo extends FeedbackSurveyQuestionBase {
  type: 'YES_NO';
}

/** One option a dropdown offers, with the same wording-ownership split as its question. */
export interface FeedbackSurveyOption {
  value: string;
  label?: string | null;
  labelKey?: string | null;
}

export interface FeedbackSurveyQuestionDropdown extends FeedbackSurveyQuestionBase {
  type: 'DROPDOWN';
  options: FeedbackSurveyOption[];
}

export interface FeedbackSurveyQuestionText extends FeedbackSurveyQuestionBase {
  type: 'TEXT';
}

export interface FeedbackSurveyQuestionTextarea extends FeedbackSurveyQuestionBase {
  type: 'TEXTAREA';
}

export type LikertOptionValue = 'STRONGLY_AGREE' | 'AGREE' | 'NEUTRAL' | 'DISAGREE' | 'STRONGLY_DISAGREE';

export interface FeedbackSurveyQuestionLikert extends FeedbackSurveyQuestionBase {
  type: 'LIKERT';
  likertOptions: LikertOptionValue[];
}

export type FeedbackSurveyQuestion
  = | FeedbackSurveyQuestionScale
    | FeedbackSurveyQuestionYesNo
    | FeedbackSurveyQuestionDropdown
    | FeedbackSurveyQuestionText
    | FeedbackSurveyQuestionTextarea
    | FeedbackSurveyQuestionLikert;

export const LIKERT_I18N_KEYS: Record<LikertOptionValue, string> = {
  STRONGLY_AGREE: 'unified.common.likert.stronglyAgree',
  AGREE: 'unified.common.likert.agree',
  NEUTRAL: 'unified.common.likert.neutral',
  DISAGREE: 'unified.common.likert.disagree',
  STRONGLY_DISAGREE: 'unified.common.likert.stronglyDisagree',
};

/**
 * The i18n key for a likert answer as it comes back out of storage.
 *
 * Answers are written lowercased (`strongly_agree`) while the schema names the options in the
 * enum's own case, so anything reading answers back — the teacher's feedback tab, a report — has to
 * cross that gap. Returns null for a value no longer in the scale, which is what a stored answer
 * from a retired option looks like.
 */
export const likertI18nKeyForAnswer = (value: string): string | null =>
  LIKERT_I18N_KEYS[value.toUpperCase() as LikertOptionValue] ?? null;

/**
 * A question's wording, whoever owns it.
 *
 * One resolver rather than a `labelKey ?? label` at each of the eight surfaces that render a
 * question: a surface that forgot the key would print a blank heading over an answerable control,
 * and a survey nobody can read the questions of still submits.
 */
export const feedbackQuestionLabel = (
  question: { label?: string | null; labelKey?: string | null },
  translate: (key: string) => string,
): string => (question.labelKey ? translate(question.labelKey) : question.label ?? '');

/** An option's wording, on the same rule as the question that offers it. */
export const feedbackOptionLabel = (
  option: FeedbackSurveyOption,
  translate: (key: string) => string,
): string => (option.labelKey ? translate(option.labelKey) : option.label ?? option.value);

/**
 * Says a payload could not be read, without saying what was in it.
 *
 * The fallback renders as "every question unanswered", which is indistinguishable from a student
 * who answered nothing — the exact confusion that hid a transport bug on the teacher's tab until it
 * was found by hand. So it is announced rather than swallowed.
 *
 * Deliberately logs the length and nothing else: this payload is a named student's self-disclosure
 * about their confidence and career plans, and a console line is the wrong place for it.
 */
const reportUnreadableResponses = (reason: string, raw: string): void => {
  console.warn(`parseFeedbackResponses: ${reason}; rendering as unanswered (${raw.length} chars)`);
};

/**
 * A stored answer set, whatever shape the transport handed it over in.
 *
 * The `JSON` scalar serialises with `json_encode`, so `responses` crosses GraphQL as a **string**
 * even though it is jsonb in the database and an array in PHP. A reader that accepts only an object
 * gets `{}` and reports every question as unanswered — which is precisely what the teacher's
 * feedback tab did until this was shared, while the content admin's table beside it worked because
 * it happened to handle both shapes.
 */
export const parseFeedbackResponses = (raw: unknown): Record<string, unknown> => {
  if (typeof raw === 'object' && raw !== null) {
    return raw as Record<string, unknown>;
  }

  if (typeof raw !== 'string') {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as Record<string, unknown>;
    }

    // Parsed, but not into answers. Same silent `{}` as a parse failure, so it is reported the
    // same way rather than being the one shape that degrades quietly.
    reportUnreadableResponses('parsed to a non-object', raw);
    return {};
  }
  catch {
    reportUnreadableResponses('failed to parse', raw);
    return {};
  }
};

/** The shape any answer reader needs off a question: enough to decode a stored value. */
export interface FeedbackAnswerQuestion {
  type: string;
  options?: FeedbackSurveyOption[] | null;
}

/**
 * A stored feedback answer as a person reads it.
 *
 * Every surface that reads answers back hits the same problem — what is on file is a bare value
 * (`strongly_agree`, `good`, `true`, `8`) whose meaning lives in the question. Decoding it in each
 * reader is how the teacher's tab and the content admin's table came to disagree about which types
 * they knew how to render, so there is one decoder and both call it.
 *
 * `translate` is passed in rather than imported: this file is consumed from two apps, and the
 * likert labels are the one part of an answer that is translated rather than stored.
 *
 * Returns null for an answer that was never given, so a caller can say so rather than print a blank.
 */
export const feedbackAnswerLabel = (
  question: FeedbackAnswerQuestion,
  value: unknown,
  translate: (key: string) => string,
): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (question.type === 'LIKERT') {
    const key = likertI18nKeyForAnswer(String(value));
    return key ? translate(key) : String(value);
  }
  if (question.type === 'YES_NO') {
    return translate(value ? 'unified.common.yes' : 'unified.common.no');
  }
  if (question.type === 'DROPDOWN') {
    const option = question.options?.find(candidate => candidate.value === value);
    return option ? feedbackOptionLabel(option, translate) : String(value);
  }
  return String(value);
};
